"use client";

import Image from "next/image";
import { useRef, useState } from "react";
import { CardButtons, CardPriceTag } from "../../components/ProductCard";
import classes from "./ProductSliderSection.module.css";

import "swiper/css";
import { Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperType } from "swiper/types";

import rect from "@/assets/rect.png";
import RatingStars from "@/components/RatingStars";
import { useLogicalProductsManual } from "@/hooks/useLogicalProducts";
import { convertNameChar, getImageArray, getSectionStyle } from "@/utils";
import { getS3ImageUrl } from "@/utils/routes";

type Breakpoint = 0 | 500 | 800 | 1024 | 1400;

const descritions = [
  "Lightweight and portable design makes it easy to carry",
  "Fanless design makes all the operations silent",
  "Available in three colors that look sleek",
];

const slidesPerViews: {
  breakpoint: Breakpoint;
  value: number;
}[] = [
  {
    breakpoint: 0,
    value: 4,
  },
  {
    breakpoint: 500,
    value: 5,
  },
  {
    breakpoint: 800,
    value: 6,
  },
  {
    breakpoint: 1024,
    value: 7,
  },
  {
    breakpoint: 1400,
    value: 8,
  },
];

const currentSlidePerView = (): number => {
  for (let i = slidesPerViews.length - 1; i >= 0; i--) {
    if (globalThis.innerWidth * 0.9 >= slidesPerViews[i].breakpoint) {
      return slidesPerViews[i].value;
    }
  }
  return slidesPerViews[0].value;
};

interface Props extends WidgetSectionProps {}

const ProductSliderSection = ({
  widget,
  initialProducts,
}: Props & { initialProducts?: ProductItem[] }) => {
  const mainRef = useRef(null);

  const { products, loading, error } = useLogicalProductsManual({
    custom: widget?.data?.singleProductSlider?.products,
    ref: mainRef,
    initialProducts,
  });

  const [activeIndex, setActiveIndex] = useState(
    Math.floor(currentSlidePerView() / 2)
  );

  const selectedProduct = products?.[activeIndex];
  const selectedSku = selectedProduct?.skus?.[0];

  const onSlideChange = (swiper: SwiperType) => {
    const midIndex = Math.floor(currentSlidePerView() / 2);
    if (
      activeIndex < swiper.activeIndex ||
      activeIndex - swiper.activeIndex >= currentSlidePerView()
    ) {
      const newIndex = swiper.activeIndex + midIndex;
      setActiveIndex(newIndex < products?.length ? newIndex : 0);
    }
  };

  // if ((!productsToShow || productsToShow.length === 0) && !loading) return null;

  const settings = widget?.data?.settings || {};

  return (
    <div
      className={classes.ProductSliderSection}
      ref={mainRef}
      style={getSectionStyle(settings)}
    >
      <div className="container">
        <div className={classes.contents}>
          <div className={classes.top}>
            {products && products.length > 0 && (
              <div className={classes.productContainer}>
                <div className={classes.left}>
                  <Image
                    src={getS3ImageUrl(
                      getImageArray(selectedProduct?.thumbnail)[0] || "",
                      "MEDIA"
                    )}
                    alt={convertNameChar(selectedProduct?.name?? 'product image')}
                    layout="responsive"
                    width={500}
                    height={500}
                    style={{
                      aspectRatio: 1,
                      objectFit: "contain",
                    }}
                    objectFit="contain"
                    objectPosition="center"
                    unoptimized={true}
                  />
                </div>
                <div className={classes.right}>
                  <h2 className={classes.title}>
                    {convertNameChar(selectedProduct?.name)}
                  </h2>

                  <CardPriceTag
                    price={selectedSku.discountedPrice || 0}
                    previousPrice={selectedSku.price}
                    className="!justify-start"
                    size="lg"
                  />

                  <RatingStars rating={4.1} />

                  <ul className={classes.descriptions}>
                    {descritions.map((description, index) => (
                      <li key={index} className={classes.description}>
                        {description}
                      </li>
                    ))}
                  </ul>

                  {/* <CardButtons type="md" /> */}
                </div>
              </div>
            )}
            {/* {productsToShow && productsToShow.length > 0 && (
                            <ProductCard
                                key={productsToShow[activeIndex].id +'-'+ activeIndex}
                                product={productsToShow[activeIndex]}
                                type="full"
                                settings={settings}
                            />
                        )} */}
          </div>

          <div className={`${classes.bottom} ${classes.slider}`}>
            <Swiper
              spaceBetween={6}
              slidesPerView={slidesPerViews[0].value}
              breakpoints={{
                500: {
                  slidesPerView: slidesPerViews[1].value,
                  // spaceBetween: 8,
                },
                800: {
                  slidesPerView: slidesPerViews[2].value,
                  // spaceBetween: 12,
                },
                1024: {
                  slidesPerView: slidesPerViews[3].value,
                  // spaceBetween: 16,
                },
                1400: {
                  slidesPerView: slidesPerViews[4].value,
                },
              }}
              modules={[Autoplay]}
              className={classes.swiper}
              onSlideChange={onSlideChange}
            >
              {products.map((product, index) => (
                <SwiperSlide
                  key={product.slug || product.id}
                  style={{
                    height: "auto",
                  }}
                >
                  <div
                    onClick={() => setActiveIndex(index)}
                    className={
                      classes.item +
                      " " +
                      (index === activeIndex ? classes.active : "")
                    }
                  >
                    <div className={classes.bgContainer}>
                      <Image
                        src={rect}
                        alt="bg"
                        layout="responsive"
                        width={500}
                        height={500}
                        style={{
                          aspectRatio: 1,
                        }}
                        className={classes.bgImg}
                        unoptimized={true}
                      />

                      <div className={classes.image}>
                        <Image
                          src={getS3ImageUrl(
                            getImageArray(product.thumbnail)[0] || "",
                            "MEDIA"
                          )}
                          alt={product.name?? 'product image'}
                          layout="fill"
                          objectFit="cover"
                          objectPosition="center"
                          unoptimized={true}
                        />
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>

        {/* {(isIntersecting && !loading) ? (
                ) : (
                    <ProductLargSkleton />
                )} */}
      </div>
    </div>
  );
};

export default ProductSliderSection;
